import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoWithText } from '../components/Logo';
import { ArrowRight, List, X, BookOpen, ChartBar, Users, ChatCircle, Truck, House, CheckCircle, ShieldCheck } from 'phosphor-react';
import './LandingPage.css';

const modules = [
  {
    icon: BookOpen,
    title: 'Academic Management',
    description: 'Streamline all academic operations from enrolment to graduation.',
    features: ['Student records with photo uploads', 'Class & section management', 'Attendance tracking (daily & periodic)', 'Grade management & report cards', 'Timetable creation', 'Assignment submission & rubric grading', 'Bulk student import via CSV'],
  },
  {
    icon: ChartBar,
    title: 'Finance & Accounting',
    description: 'Professional accounting suite with enterprise-grade features.',
    features: ['Chart of Accounts management', 'Double-entry journal entries', 'Expense tracking & approvals', 'P&L, Balance Sheet, Trial Balance', 'Multi-currency (GHS) support', 'Budget tracking & analysis', 'Year-end closing & reconciliation'],
  },
  {
    icon: Users,
    title: 'Billing & Revenue',
    description: 'Complete billing solution purpose-built for schools.',
    features: ['Flexible fee structures & categories', 'Bulk discounts & late fees', 'Automated SMS payment reminders', 'Online payments via Paystack', 'Invoice generation & tracking', 'Subscription management', 'Payment reconciliation'],
  },
  {
    icon: Users,
    title: 'Payroll Management',
    description: 'Streamlined staff salary processing.',
    features: ['Staff record management', 'Salary structure configuration', 'Allowances & deductions', 'Payslip generation', 'PAYE tax calculation', 'Bulk salary processing', 'Payroll reports & analysis'],
  },
  {
    icon: Truck,
    title: 'Transport Management',
    description: 'Fleet & student transportation — including live GPS tracking.',
    features: ['Vehicle fleet management', 'Route creation & optimisation', 'Driver assignment & tracking', 'Student transport enrolment', 'Daily attendance tracking', 'Real-time GPS integration', 'Transport billing & maintenance logs'],
  },
  {
    icon: House,
    title: 'Hostel Management',
    description: 'Complete residential facility management.',
    features: ['Multi-hostel configuration', 'Room allocation & management', 'Attendance & check-in/out', 'Visitor management & logs', 'Complaint & issue tracking', 'Maintenance requests', 'Hostel fee billing'],
  },
  {
    icon: ChatCircle,
    title: 'Communication Hub',
    description: 'Real-time communication between all school stakeholders.',
    features: ['Parent-teacher messaging', 'SMS notification system', 'Fee reminder campaigns', 'Attendance alerts', 'Announcement broadcasting', 'Multi-recipient SMS', 'Message templates & history'],
  },
  {
    icon: ShieldCheck,
    title: 'Student Security',
    description: 'QR-based pickup system with live tracking for school dismissal.',
    features: ['QR token generation per student', 'Officer scan & verify at gate', 'Parent collector live tracking', 'Bus GPS & route monitoring', 'Admin tracking dashboard', 'Daily status reset & audit trail', 'Role-based access for officers & drivers'],
  },
];

export default function FeaturesPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [['/', 'Home'], ['/about', 'About'], ['/documentation', 'Docs'], ['/contact', 'Contact']];

  return (
    <div style={{ background: 'var(--land-white)', color: 'var(--land-text)', fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* Nav */}
      <nav className="lp-nav" style={{ background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid var(--land-border)' }}>
        <div className="lp-nav-inner">
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <LogoWithText size={30} />
          </button>
          <div className="lp-nav-links">
            {navLinks.map(([path, label]) => (
              <button key={path} className="lp-nav-link" onClick={() => navigate(path)}>{label}</button>
            ))}
          </div>
          <div className="lp-nav-actions">
            <button className="lp-btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="lp-btn-primary" onClick={() => navigate('/contact')}>
              Request a Demo <ArrowRight size={15} weight="bold" />
            </button>
          </div>
          <button onClick={() => setMenuOpen(true)} className="lp-hamburger" style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <List size={26} color="var(--land-text)" />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="lp-mobile-menu">
          <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}><X size={20} /></button>
          {navLinks.map(([path, label]) => (
            <button key={path} className="lp-mobile-link" onClick={() => { navigate(path); setMenuOpen(false); }}>{label}</button>
          ))}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 24, borderTop: '1px solid var(--land-border)' }}>
            <button className="lp-btn-outline-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/login'); setMenuOpen(false); }}>Sign In</button>
            <button className="lp-btn-primary-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/contact'); setMenuOpen(false); }}>Request a Demo</button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section style={{ paddingTop: 120, paddingBottom: 64, textAlign: 'center', background: 'var(--land-off)', borderBottom: '1px solid var(--land-border)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px' }}>
          <p className="lp-section-label" style={{ justifyContent: 'center', display: 'flex' }}>Platform Features</p>
          <h1 style={{ fontSize: 'clamp(32px,5vw,52px)', fontWeight: 800, color: 'var(--land-text)', letterSpacing: '-0.03em', fontFamily: 'Manrope, sans-serif', marginBottom: 16, lineHeight: 1.1 }}>
            Everything a school needs, in one platform.
          </h1>
          <p style={{ fontSize: 18, color: 'var(--land-muted)', lineHeight: 1.65 }}>
            Eight fully integrated modules covering every aspect of school operations — from academics to security.
          </p>
        </div>
      </section>

      {/* Module grid */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 }}>
          {modules.map(({ icon: Icon, title, description, features }) => (
            <div key={title} style={{ padding: 32, background: 'var(--land-stone)', borderRadius: 14, border: '1px solid var(--land-border)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ width: 44, height: 44, background: 'var(--land-forest)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', marginBottom: 20, flexShrink: 0 }}>
                <Icon size={22} weight="duotone" />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--land-text)', fontFamily: 'Manrope, sans-serif', marginBottom: 8 }}>{title}</h3>
              <p style={{ fontSize: 14, color: 'var(--land-muted)', lineHeight: 1.6, marginBottom: 16 }}>{description}</p>
              <div style={{ marginBottom: 16, display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 999, background: '#ecfdf5', color: '#166534', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', width: 'fit-content' }}>
                Available
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
                {features.map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--land-muted)' }}>
                    <CheckCircle size={14} weight="fill" color="var(--land-moss)" style={{ flexShrink: 0, marginTop: 2 }} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="lp-cta-band">
        <h2 className="lp-cta-h2">See every feature in action.</h2>
        <p className="lp-cta-sub">Book a personalised walkthrough with our team and get your school set up in 48 hours.</p>
        <button className="lp-btn-cta" onClick={() => navigate('/contact')}>
          Request a Demo <ArrowRight size={18} weight="bold" />
        </button>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--land-border)', padding: '24px 32px', textAlign: 'center', background: 'var(--land-off)' }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--land-muted)' }}>
          &larr; Back to Campusio
        </button>
      </div>
    </div>
  );
}
